import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Input,
  FormGroup,
  Label,
  Row,
  Col,
  Spinner
} from 'reactstrap';
import Select from 'react-select';
import { Search, X } from 'lucide-react';
import { advancedSearchCustomers } from './Api/GmsCustomer';
import { getCountries } from './Api/GmsCountry';
import { getBranches } from './Api/GmsBranch';

const CustomerAdvancedSearchModal = ({ 
  isOpen, 
  toggle, 
  onCustomerSelected,
  userBranchId = null  // Branch ID of logged-in user
}) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);
  const [countries, setCountries] = useState([]);
  const [branches, setBranches] = useState([]);
  
  // Ref for results section to enable auto-scroll
  const resultsRef = useRef(null);

  // Filter state
  const [filters, setFilters] = useState({
    customer_NameEn: '',
    customer_NameAr: '',
    customer_ContactNo: '',
    customer_Country_Id: null,
    customer_Email: '',
    customer_Code: '',
    customer_ID_Number: '',
    customer_TRN: '',
    company_Name: '',
    primaryContact_Name: '',
    primaryContact_Phone: '',
    customer_Type: null,
    customer_Branch_Id: null,
    customer_Active: true, // Default to active only
    customer_IsInternational: null
  });

  // Load countries and branches on mount
  useEffect(() => {
    if (isOpen) {
      loadCountries();
      loadBranches();
      // Reset filters and search when modal opens
      resetFilters();
      setCurrentPage(1);
      setCustomers([]);
      setTotalCount(0);
    }
  }, [isOpen]);

  const loadCountries = async () => {
    try {
      const data = await getCountries({});
      setCountries(data || []);
    } catch (error) {
      console.error("Error loading countries:", error);
      setCountries([]);
    }
  };

  const loadBranches = async () => {
    try {
      const data = await getBranches({ branch_Active: true });
      setBranches(data || []);
    } catch (error) {
      console.error("Error loading branches:", error);
      setBranches([]);
    }
  };

  const resetFilters = () => {
    setFilters({
      customer_NameEn: '',
      customer_NameAr: '',
      customer_ContactNo: '',
      customer_Country_Id: null,
      customer_Email: '',
      customer_Code: '',
      customer_ID_Number: '',
      customer_TRN: '',
      company_Name: '',
      primaryContact_Name: '',
      primaryContact_Phone: '',
      customer_Type: null,
      customer_Branch_Id: null,
      customer_Active: true, // Default to active only
      customer_IsInternational: null
    });
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle Enter key press to trigger search
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch(1);
    }
  };

  // Handle dropdown change with auto-search
  const handleDropdownChange = (field, option) => {
    handleFilterChange(field, option?.value || null);
    // Auto-trigger search when dropdown value changes
    setTimeout(() => {
      handleSearch(1);
    }, 100);
  };

  const handleSearch = async (page = 1) => {
    setLoading(true);
    try {
      const response = await advancedSearchCustomers(filters, page, pageSize, userBranchId);
      setCustomers(response.items || []);
      setTotalCount(response.totalCount || 0);
      setCurrentPage(page);
      
      // Auto-scroll to results after search completes
      setTimeout(() => {
        if (resultsRef.current) {
          resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } catch (error) {
      console.error("Error searching customers:", error);
      setCustomers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    resetFilters();
    setCurrentPage(1);
    setCustomers([]);
    setTotalCount(0);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= Math.ceil(totalCount / pageSize)) {
      handleSearch(newPage);
    }
  };

  const handleCustomerSelect = (customer) => {
    if (onCustomerSelected) {
      onCustomerSelected(customer);
    }
    toggle();
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  // Country options
  const countryOptions = countries.map(country => ({
    value: country.country_Id,
    label: `${country.country_NameEn}${country.country_NameAr ? ' - ' + country.country_NameAr : ''}`
  }));

  // Branch options
  const branchOptions = branches.map(branch => ({
    value: branch.branch_Id,
    label: branch.branch_NameEn || branch.branch_Name
  }));

  // Customer Type options
  const customerTypeOptions = [
    { value: 1, label: 'Individual' },
    { value: 2, label: 'Business' }
  ];

  // Active/Inactive options
  const activeStatusOptions = [
    { value: true, label: 'Active' },
    { value: false, label: 'Inactive' },
    { value: null, label: 'All' }
  ];

  // International options
  const internationalOptions = [
    { value: true, label: 'International' },
    { value: false, label: 'Local' },
    { value: null, label: 'All' }
  ];

  // Custom styles for Select components
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '38px',
      borderColor: state.isFocused ? '#556ee6' : '#ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '200px',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
      color: state.isSelected ? 'white' : '#495057',
      '&:hover': {
        backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
      },
    }),
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" centered>
      <ModalHeader toggle={toggle}>
        <div className="d-flex align-items-center gap-2">
          <Search size={20} />
          <span>Advanced Customer Search</span>
        </div>
      </ModalHeader>
      <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {/* Filters Section */}
        <div className="mb-4">
          <Row>
            <Col md={3}>
              <FormGroup>
                <Label>Name (English)</Label>
                <Input
                  type="text"
                  value={filters.customer_NameEn}
                  onChange={(e) => handleFilterChange('customer_NameEn', e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter name in English"
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Name (Arabic)</Label>
                <Input
                  type="text"
                  value={filters.customer_NameAr}
                  onChange={(e) => handleFilterChange('customer_NameAr', e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter name in Arabic"
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Mobile Number</Label>
                <Input
                  type="text"
                  value={filters.customer_ContactNo}
                  onChange={(e) => handleFilterChange('customer_ContactNo', e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter mobile number"
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={filters.customer_Email}
                  onChange={(e) => handleFilterChange('customer_Email', e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter email"
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={3}>
              <FormGroup>
                <Label>Customer Code</Label>
                <Input
                  type="text"
                  value={filters.customer_Code}
                  onChange={(e) => handleFilterChange('customer_Code', e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter customer code"
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>ID Number</Label>
                <Input
                  type="text"
                  value={filters.customer_ID_Number}
                  onChange={(e) => handleFilterChange('customer_ID_Number', e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter ID number"
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>TRN</Label>
                <Input
                  type="text"
                  value={filters.customer_TRN}
                  onChange={(e) => handleFilterChange('customer_TRN', e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter TRN"
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Nationality</Label>
                <Select
                  options={countryOptions}
                  value={countryOptions.find(opt => opt.value === filters.customer_Country_Id) || null}
                  onChange={(option) => handleDropdownChange('customer_Country_Id', option)}
                  isClearable
                  placeholder="Select nationality"
                  styles={selectStyles}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={3}>
              <FormGroup>
                <Label>Company Name</Label>
                <Input
                  type="text"
                  value={filters.company_Name}
                  onChange={(e) => handleFilterChange('company_Name', e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter company name"
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Primary Contact Name</Label>
                <Input
                  type="text"
                  value={filters.primaryContact_Name}
                  onChange={(e) => handleFilterChange('primaryContact_Name', e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter primary contact name"
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Primary Contact Phone</Label>
                <Input
                  type="text"
                  value={filters.primaryContact_Phone}
                  onChange={(e) => handleFilterChange('primaryContact_Phone', e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter primary contact phone"
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Customer Type</Label>
                <Select
                  options={customerTypeOptions}
                  value={customerTypeOptions.find(opt => opt.value === filters.customer_Type) || null}
                  onChange={(option) => handleDropdownChange('customer_Type', option)}
                  isClearable
                  placeholder="Select customer type"
                  styles={selectStyles}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={3}>
              <FormGroup>
                <Label>Branch</Label>
                <Select
                  options={branchOptions}
                  value={branchOptions.find(opt => opt.value === filters.customer_Branch_Id) || null}
                  onChange={(option) => handleDropdownChange('customer_Branch_Id', option)}
                  isClearable
                  placeholder="Select branch"
                  styles={selectStyles}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Status</Label>
                <Select
                  options={activeStatusOptions}
                  value={activeStatusOptions.find(opt => opt.value === filters.customer_Active) || { value: true, label: 'Active' }}
                  onChange={(option) => handleDropdownChange('customer_Active', option)}
                  placeholder="Select status"
                  styles={selectStyles}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>International/Local</Label>
                <Select
                  options={internationalOptions}
                  value={internationalOptions.find(opt => opt.value === filters.customer_IsInternational) || null}
                  onChange={(option) => handleDropdownChange('customer_IsInternational', option)}
                  isClearable
                  placeholder="Select type"
                  styles={selectStyles}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col md={12} className="d-flex gap-2">
              <Button color="primary" onClick={() => handleSearch(1)} disabled={loading}>
                {loading ? <Spinner size="sm" /> : <><Search size={16} className="me-1" /> Search</>}
              </Button>
              <Button color="secondary" onClick={handleReset}>
                <X size={16} className="me-1" /> Reset
              </Button>
            </Col>
          </Row>
        </div>

        {/* Results Section */}
        {customers.length > 0 && (
          <div className="mt-4" ref={resultsRef}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6>Results ({totalCount} found)</h6>
            </div>
            <Table striped hover responsive>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Mobile</th>
                  <th>Customer Type</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => {
                  const customerTypeLabel = customer.customer_Type === 1 ? 'Individual' : customer.customer_Type === 2 ? 'Business' : 'N/A';
                  return (
                    <tr key={customer.customer_Id} style={{ cursor: 'pointer' }} onClick={() => handleCustomerSelect(customer)}>
                      <td>{customer.customer_NameEn || 'N/A'}</td>
                      <td>{customer.customer_ContactNo || 'N/A'}</td>
                      <td>{customerTypeLabel}</td>
                      <td>
                        <Button color="primary" size="sm" onClick={(e) => {
                          e.stopPropagation();
                          handleCustomerSelect(customer);
                        }}>
                          Select
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
                </div>
                <div className="d-flex gap-2">
                  <Button 
                    color="secondary" 
                    size="sm" 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || loading}
                  >
                    Previous
                  </Button>
                  <span className="align-self-center">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button 
                    color="secondary" 
                    size="sm" 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || loading}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {!loading && customers.length === 0 && totalCount === 0 && (
          <div className="text-center text-muted py-4">
            No customers found. Please adjust your filters and search again.
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>Close</Button>
      </ModalFooter>
    </Modal>
  );
};

export default CustomerAdvancedSearchModal;
